// Helper para operaciones de base de datos con tipos correctos
// Estructura: 4 Unidades → 4 Lecciones → 4 Contenidos → 12 Ejercicios
import { supabase } from './supabase'
import type {
  Unit,
  Lesson,
  Content,
  ContentMaterial,
  Exercise,
  ExerciseOption,
  ExerciseAnswer,
  ExerciseResult,
  StudentUnitProgress,
  StudentLessonProgress,
  StudentContentProgress,
  UnitWorkshop,
  WorkshopAttendance,
  UnitExam,
  ExamQuestion,
  ExamAttempt,
  FinalExam,
  FinalExamAttempt,
  Badge,
  StudentBadge,
  PointTransaction,
  Certificate,
  Profile,
  Student,
  Teacher,
  Notification,
  SkillType,
  UnitWithProgress,
  LessonWithProgress,
  ContentWithProgress,
  StudentStats,
  LeaderboardEntry,
  Cohort,
  WorkshopContent,
  WorkshopBlock,
  AutomationFlow,
  AutomationAction,
  AutomationLog,
  Survey,
  SurveyResponse,
  StudentPopup
} from '@/types'

// Tipo genérico para respuestas
type DbResult<T> = { data: T | null; error: Error | null }

// ============================================
// MAPEO DB → FRONTEND
// La BD usa columnas bilingües (name_en/name_es, title_en/title_es)
// El frontend espera columnas simples (title, description, order_num)
// ============================================

function mapUnit(raw: any): any {
  if (!raw) return raw
  return {
    ...raw,
    title: raw.title || raw.name_es || raw.name_en || `Unidad ${raw.unit_number || ''}`,
    description: raw.description || raw.description_es || raw.description_en || '',
    order_num: raw.order_num ?? raw.unit_number ?? 0,
    objectives: raw.objectives || raw.objectives_es || raw.objectives_en || [],
  }
}

function mapLesson(raw: any): any {
  if (!raw) return raw
  return {
    ...raw,
    title: raw.title || raw.title_es || raw.title_en || `Lección ${raw.lesson_number || ''}`,
    description: raw.description || raw.description_es || raw.description_en || '',
    order_num: raw.order_num ?? raw.lesson_number ?? raw.order_index ?? 0,
    // Si tiene unit anidado, mapearlo también
    unit: raw.unit ? mapUnit(raw.unit) : raw.unit,
  }
}

function mapContent(raw: any): any {
  if (!raw) return raw
  return {
    ...raw,
    title: raw.title || raw.title_es || raw.title_en || `Contenido ${raw.content_number || ''}`,
    description: raw.description || raw.description_es || raw.description_en || '',
    order_num: raw.order_num ?? raw.content_number ?? raw.order_index ?? 0,
    theory_content: raw.theory_content || raw.theory_es || raw.theory_en || '',
  }
}

// ============================================
// UNIDADES
// ============================================

export async function getUnits(): Promise<DbResult<Unit[]>> {
  const { data, error } = await supabase
    .from('units')
    .select('*')
    .eq('is_active', true)
    .order('unit_number')
  return { data: data ? (data as any[]).map(mapUnit) as Unit[] : null, error }
}

export async function getUnit(unitId: string): Promise<DbResult<Unit>> {
  const { data, error } = await supabase
    .from('units')
    .select('*')
    .eq('id', unitId)
    .single()
  return { data: data ? mapUnit(data) as Unit : null, error }
}

export async function getUnitWithLessons(unitId: string): Promise<DbResult<Unit>> {
  const { data, error } = await supabase
    .from('units')
    .select(`
      *,
      lessons (
        *
      )
    `)
    .eq('id', unitId)
    .eq('is_active', true)
    .single()
  const mapped = data ? mapUnit(data) : null
  if (mapped?.lessons) {
    mapped.lessons = (mapped.lessons as any[]).map(mapLesson)
  }
  return { data: mapped as Unit | null, error }
}

export async function getUnitsWithProgress(studentId: string, profileId?: string): Promise<DbResult<UnitWithProgress[]>> {
  // Necesitamos el profile_id del ALUMNO (no del usuario que consulta) porque
  // cohorts.student_ids guarda profile_ids. Si no se pasa, caemos a auth.uid()
  // por compatibilidad con el flujo del alumno mirando su propio progreso.
  // El profesor mirando otro alumno DEBE pasar el profile_id del alumno.
  let targetProfileId = profileId
  if (!targetProfileId) {
    const { data: { user } } = await supabase.auth.getUser()
    targetProfileId = user?.id
  }
  if (!targetProfileId) {
    return { data: [], error: null }
  }

  const [unitsRes, progressRes, cohortsRes] = await Promise.all([
    supabase
      .from('units')
      .select('*')
      .eq('is_active', true)
      .order('unit_number'),
    supabase
      .from('student_unit_progress')
      .select('*')
      .eq('student_id', studentId),
    supabase
      .from('cohorts')
      .select('id, level_id, unlocked_unit_index')
      .eq('is_active', true)
      .contains('student_ids', [targetProfileId])
  ])

  if (unitsRes.error) return { data: null, error: unitsRes.error }
  if (progressRes.error) return { data: null, error: progressRes.error }
  if (cohortsRes.error) {
    console.warn('Error fetching cohorts:', cohortsRes.error.message)
  }

  const allUnits = (unitsRes.data as any[] || []).map(mapUnit) as unknown as Unit[]
  const progressData = progressRes.data as unknown as StudentUnitProgress[]
  const cohorts = (cohortsRes.data as { id: string; level_id: string | null; unlocked_unit_index: number }[] | null) || []

  // Per-level unlock map: si el alumno tiene varias cohortes en el mismo nivel,
  // usamos el mayor unlocked_unit_index disponible para ese nivel.
  const levelUnlockMap = new Map<string, number>()
  for (const c of cohorts) {
    if (!c.level_id) continue
    const current = levelUnlockMap.get(c.level_id) ?? -1
    if (c.unlocked_unit_index > current) levelUnlockMap.set(c.level_id, c.unlocked_unit_index)
  }

  const studentLevelIds = new Set(Array.from(levelUnlockMap.keys()))

  // Sin cohorte = sin acceso. Aplica incluso a alumnos pagados que aun no fueron
  // asignados: deben ser asignados manualmente para ver contenido.
  if (studentLevelIds.size === 0) {
    return { data: [], error: null }
  }

  // Solo unidades de los niveles donde el alumno esta en cohorte.
  const visibleUnits = allUnits.filter(u => u.level_id && studentLevelIds.has(u.level_id))

  // Agrupar por nivel para indexar la posicion DENTRO del nivel (no global).
  const unitsByLevel = new Map<string, Unit[]>()
  for (const u of visibleUnits) {
    const key = u.level_id as string
    const arr = unitsByLevel.get(key) ?? []
    arr.push(u)
    unitsByLevel.set(key, arr)
  }

  const unitsWithProgress: UnitWithProgress[] = []
  for (const [levelKey, levelUnits] of unitsByLevel) {
    const cohortUnlockIndex = levelUnlockMap.get(levelKey) ?? -1

    levelUnits.forEach((unit, idx) => {
      // Unidades futuras (aun no desbloqueadas por la cohorte): invisibles.
      if (idx > cohortUnlockIndex) return

      const unitProgress = progressData?.find(p => p.unit_id === unit.id)

      let finalProgress = unitProgress
      if (unitProgress && unitProgress.status === 'locked') {
        finalProgress = { ...unitProgress, status: 'available' as any }
      }

      unitsWithProgress.push({
        ...unit,
        progress: finalProgress || {
          id: '',
          student_id: studentId,
          unit_id: unit.id,
          status: 'available',
          lessons_completed: 0,
          total_lessons: 0,
          started_at: null,
          completed_at: null,
          updated_at: ''
        }
      } as UnitWithProgress)
    })
  }

  return { data: unitsWithProgress, error: null }
}

// ============================================
// LECCIONES
// ============================================

export async function getLessonsByUnit(unitId: string): Promise<DbResult<Lesson[]>> {
  const { data, error } = await supabase
    .from('lessons')
    .select('*')
    .eq('unit_id', unitId)
    .eq('is_active', true)
    .order('lesson_number')
  return { data: data ? (data as any[]).map(mapLesson) as Lesson[] : null, error }
}

export async function getLesson(lessonId: string): Promise<DbResult<Lesson>> {
  const { data, error } = await supabase
    .from('lessons')
    .select(`
      *,
      unit:units(*)
    `)
    .eq('id', lessonId)
    .single()
  return { data: data ? mapLesson(data) as Lesson : null, error }
}

export async function getLessonWithContents(lessonId: string): Promise<DbResult<Lesson>> {
  const { data, error } = await supabase
    .from('lessons')
    .select(`
      *,
      unit:units(*),
      contents(*)
    `)
    .eq('id', lessonId)
    .order('content_number', { referencedTable: 'contents' })
    .single()
  const mapped = data ? mapLesson(data) : null
  if (mapped?.contents) {
    mapped.contents = (mapped.contents as any[]).map(mapContent)
  }
  return { data: mapped as Lesson | null, error }
}

export async function getLessonsWithProgress(studentId: string, unitId: string): Promise<DbResult<LessonWithProgress[]>> {
  const { data: lessons, error: lessonsError } = await supabase
    .from('lessons')
    .select('*')
    .eq('unit_id', unitId)
    .eq('is_active', true)
    .order('lesson_number')

  if (lessonsError) return { data: null, error: lessonsError }

  const lessonsData = (lessons as any[] || []).map(mapLesson) as unknown as Lesson[]

  const { data: progress, error: progressError } = await supabase
    .from('student_lesson_progress')
    .select('*')
    .eq('student_id', studentId)
    .in('lesson_id', lessonsData?.map(l => l.id) || [])

  if (progressError) return { data: null, error: progressError }

  const progressData = progress as unknown as StudentLessonProgress[]

  // Determinar que lecciones estan disponibles
  // Una leccion esta disponible si:
  // - Es la primera leccion, o
  // - La leccion anterior esta completada
  const lessonsWithProgress = lessonsData?.map((lesson, index) => {
    const lessonProgress = progressData?.find(p => p.lesson_id === lesson.id)

    // Verificar si la leccion anterior esta completada
    let defaultStatus: string = 'locked'
    if (index === 0) {
      defaultStatus = 'available'
    } else {
      const prevLessonProgress = progressData?.find(p => p.lesson_id === lessonsData[index - 1].id)
      if (prevLessonProgress?.status === 'completed') {
        defaultStatus = 'available'
      }
    }

    return {
      ...lesson,
      progress: lessonProgress || {
        id: '',
        student_id: studentId,
        lesson_id: lesson.id,
        status: defaultStatus,
        contents_completed: 0,
        total_contents: 0,
        started_at: null,
        completed_at: null,
        updated_at: ''
      }
    } as LessonWithProgress
  }) || []

  return { data: lessonsWithProgress, error: null }
}

// ============================================
// CONTENIDOS
// ============================================

export async function getContentsByLesson(lessonId: string): Promise<DbResult<Content[]>> {
  const { data, error } = await supabase
    .from('contents')
    .select('*')
    .eq('lesson_id', lessonId)
    .eq('is_active', true)
    .order('content_number')
  return { data: data ? (data as any[]).map(mapContent) as Content[] : null, error }
}

export async function getContent(contentId: string): Promise<DbResult<Content>> {
  const { data, error } = await supabase
    .from('contents')
    .select(`
      *,
      lesson:lessons(
        *,
        unit:units(*)
      )
    `)
    .eq('id', contentId)
    .single()
  const mapped = data ? mapContent(data) : null
  if (mapped?.lesson) {
    mapped.lesson = mapLesson(mapped.lesson)
  }
  return { data: mapped as Content | null, error }
}

export async function getContentWithExercises(contentId: string): Promise<DbResult<ContentWithProgress>> {
  const { data, error } = await supabase
    .from('contents')
    .select(`
      *,
      lesson:lessons(
        *,
        unit:units(*)
      ),
      exercises(
        *,
        options:exercise_options(*),
        answers:exercise_answers(*)
      ),
      materials:content_materials(*)
    `)
    .eq('id', contentId)
    .single()

  if (error) return { data: null, error }

  // Mapear contenido y sus relaciones
  const mappedData = mapContent(data)
  if (mappedData?.lesson) {
    mappedData.lesson = mapLesson(mappedData.lesson)
  }

  // Organizar ejercicios por habilidad
  const exercises = (mappedData as Content & { exercises: Exercise[] }).exercises || []
  const exercisesBySkill = {
    speaking: exercises.filter(e => e.skill === 'speaking'),
    listening: exercises.filter(e => e.skill === 'listening'),
    writing: exercises.filter(e => e.skill === 'writing'),
    reading: exercises.filter(e => e.skill === 'reading')
  }

  const contentWithProgress: ContentWithProgress = {
    ...(mappedData as Content),
    progress: {
      id: '',
      student_id: '',
      content_id: contentId,
      status: 'not_started',
      speaking_completed: 0,
      listening_completed: 0,
      writing_completed: 0,
      reading_completed: 0,
      total_exercises_completed: 0,
      total_exercises: 12,
      points_earned: 0,
      time_spent_seconds: 0,
      started_at: null,
      completed_at: null,
      created_at: '',
      updated_at: ''
    },
    exercisesBySkill
  }

  return { data: contentWithProgress, error: null }
}

export async function getContentMaterials(contentId: string): Promise<DbResult<ContentMaterial[]>> {
  const { data, error } = await supabase
    .from('content_materials')
    .select('*')
    .eq('content_id', contentId)
    .order('order_index')
  return { data: data as ContentMaterial[] | null, error }
}

// ============================================
// EJERCICIOS
// ============================================

export async function getExercisesByContent(contentId: string): Promise<DbResult<Exercise[]>> {
  const { data, error } = await supabase
    .from('exercises')
    .select(`
      *,
      options:exercise_options(*),
      answers:exercise_answers(*)
    `)
    .eq('content_id', contentId)
    .eq('is_active', true)
    .order('order_index')
  return { data: data as Exercise[] | null, error }
}

export async function getExercisesBySkill(contentId: string, skill: SkillType): Promise<DbResult<Exercise[]>> {
  const { data, error } = await supabase
    .from('exercises')
    .select(`
      *,
      options:exercise_options(*),
      answers:exercise_answers(*)
    `)
    .eq('content_id', contentId)
    .eq('skill', skill)
    .eq('is_active', true)
    .order('order_index')
  return { data: data as Exercise[] | null, error }
}

export async function getExercise(exerciseId: string): Promise<DbResult<Exercise>> {
  const { data, error } = await supabase
    .from('exercises')
    .select(`
      *,
      content:contents(
        *,
        lesson:lessons(
          *,
          unit:units(*)
        )
      ),
      options:exercise_options(*),
      answers:exercise_answers(*)
    `)
    .eq('id', exerciseId)
    .single()
  return { data: data as Exercise | null, error }
}

export async function getExerciseOptions(exerciseId: string): Promise<DbResult<ExerciseOption[]>> {
  const { data, error } = await supabase
    .from('exercise_options')
    .select('*')
    .eq('exercise_id', exerciseId)
    .order('order_index')
  return { data: data as ExerciseOption[] | null, error }
}

export async function getExerciseAnswers(exerciseId: string): Promise<DbResult<ExerciseAnswer[]>> {
  const { data, error } = await supabase
    .from('exercise_answers')
    .select('*')
    .eq('exercise_id', exerciseId)
  return { data: data as ExerciseAnswer[] | null, error }
}

// ============================================
// RESULTADOS DE EJERCICIOS
// ============================================

export async function saveExerciseResult(data: {
  student_id: string
  exercise_id: string
  answer_given: string
  is_correct: boolean
  score: number
  points_earned: number
  time_taken_seconds: number
  attempt_number: number
  audio_recording_url?: string
}): Promise<DbResult<ExerciseResult>> {
  const { data: result, error } = await supabase
    .from('exercise_results')
    .insert(data as never)
    .select()
    .single()
  return { data: result as ExerciseResult | null, error }
}

export async function getExerciseResults(studentId: string, exerciseId?: string): Promise<DbResult<ExerciseResult[]>> {
  let query = supabase
    .from('exercise_results')
    .select('*')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false })

  if (exerciseId) {
    query = query.eq('exercise_id', exerciseId)
  }

  const { data, error } = await query
  return { data: data as ExerciseResult[] | null, error }
}

// ============================================
// PROGRESO DE UNIDAD
// ============================================

export async function getStudentUnitProgress(studentId: string, unitId?: string): Promise<DbResult<StudentUnitProgress[]>> {
  let query = supabase
    .from('student_unit_progress')
    .select('*')
    .eq('student_id', studentId)

  if (unitId) {
    query = query.eq('unit_id', unitId)
  }

  const { data, error } = await query.order('created_at')
  return { data: data as StudentUnitProgress[] | null, error }
}

export async function updateStudentUnitProgress(
  studentId: string,
  unitId: string,
  data: Partial<StudentUnitProgress>
): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('student_unit_progress')
    .update(data as never)
    .eq('student_id', studentId)
    .eq('unit_id', unitId)
  return { data: null, error }
}

// ============================================
// PROGRESO DE LECCIÓN
// ============================================

export async function getStudentLessonProgress(studentId: string, lessonId?: string): Promise<DbResult<StudentLessonProgress[]>> {
  let query = supabase
    .from('student_lesson_progress')
    .select('*')
    .eq('student_id', studentId)

  if (lessonId) {
    query = query.eq('lesson_id', lessonId)
  }

  const { data, error } = await query
  return { data: data as StudentLessonProgress[] | null, error }
}

export async function upsertStudentLessonProgress(
  data: Partial<StudentLessonProgress> & { student_id: string; lesson_id: string }
): Promise<DbResult<StudentLessonProgress>> {
  const { data: result, error } = await supabase
    .from('student_lesson_progress')
    .upsert(data as never, { onConflict: 'student_id,lesson_id' })
    .select()
    .single()
  return { data: result as StudentLessonProgress | null, error }
}

export async function updateStudentLessonProgress(
  studentId: string,
  lessonId: string,
  data: Partial<StudentLessonProgress>
): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('student_lesson_progress')
    .update(data as never)
    .eq('student_id', studentId)
    .eq('lesson_id', lessonId)
  return { data: null, error }
}

// ============================================
// PROGRESO DE CONTENIDO
// ============================================

export async function getStudentContentProgress(studentId: string, contentId?: string): Promise<DbResult<StudentContentProgress[]>> {
  let query = supabase
    .from('student_content_progress')
    .select('*')
    .eq('student_id', studentId)

  if (contentId) {
    query = query.eq('content_id', contentId)
  }

  const { data, error } = await query
  return { data: data as StudentContentProgress[] | null, error }
}

export async function upsertStudentContentProgress(
  data: Partial<StudentContentProgress> & { student_id: string; content_id: string }
): Promise<DbResult<StudentContentProgress>> {
  const { data: result, error } = await supabase
    .from('student_content_progress')
    .upsert(data as never, { onConflict: 'student_id,content_id' })
    .select()
    .single()
  return { data: result as StudentContentProgress | null, error }
}

export async function updateStudentContentProgress(
  studentId: string,
  contentId: string,
  data: Partial<StudentContentProgress>
): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('student_content_progress')
    .update(data as never)
    .eq('student_id', studentId)
    .eq('content_id', contentId)
  return { data: null, error }
}

// ============================================
// TALLERES
// ============================================

export async function getUnitWorkshops(): Promise<DbResult<UnitWorkshop[]>> {
  // Try with unit join only (teacher join may fail due to RLS for students)
  const { data, error } = await supabase
    .from('unit_workshops')
    .select(`
      *,
      unit:units(*)
    `)
    .eq('is_active', true)
    .order('scheduled_date', { ascending: true })

  if (error) {
    // Fallback: no joins at all
    const { data: fallback, error: fallbackError } = await supabase
      .from('unit_workshops')
      .select('*')
      .eq('is_active', true)
      .order('scheduled_date', { ascending: true })
    return { data: fallback as UnitWorkshop[] | null, error: fallbackError }
  }

  return { data: data as UnitWorkshop[] | null, error: null }
}

export async function getUnitWorkshopsForCohort(cohortId: string): Promise<DbResult<UnitWorkshop[]>> {
  const { data, error } = await supabase
    .from('unit_workshops')
    .select(`
      *,
      unit:units(*)
    `)
    .eq('is_active', true)
    .eq('cohort_id', cohortId)
    .order('scheduled_date', { ascending: true })

  if (error) {
    const { data: fallback, error: fallbackError } = await supabase
      .from('unit_workshops')
      .select('*')
      .eq('is_active', true)
      .eq('cohort_id', cohortId)
      .order('scheduled_date', { ascending: true })
    return { data: fallback as UnitWorkshop[] | null, error: fallbackError }
  }

  return { data: data as UnitWorkshop[] | null, error: null }
}

export async function getUnitWorkshopsForCohorts(cohortIds: string[]): Promise<DbResult<UnitWorkshop[]>> {
  if (cohortIds.length === 0) return { data: [], error: null }

  const { data, error } = await supabase
    .from('unit_workshops')
    .select(`
      *,
      unit:units(*)
    `)
    .eq('is_active', true)
    .in('cohort_id', cohortIds)
    .order('scheduled_date', { ascending: true })

  if (error) {
    const { data: fallback, error: fallbackError } = await supabase
      .from('unit_workshops')
      .select('*')
      .eq('is_active', true)
      .in('cohort_id', cohortIds)
      .order('scheduled_date', { ascending: true })
    return { data: fallback as UnitWorkshop[] | null, error: fallbackError }
  }

  return { data: data as UnitWorkshop[] | null, error: null }
}

export async function getWorkshopByUnit(unitId: string): Promise<DbResult<UnitWorkshop>> {
  const { data, error } = await supabase
    .from('unit_workshops')
    .select('*')
    .eq('unit_id', unitId)
    .maybeSingle()
  return { data: data as UnitWorkshop | null, error }
}

export async function getWorkshop(workshopId: string): Promise<DbResult<UnitWorkshop>> {
  const { data, error } = await supabase
    .from('unit_workshops')
    .select(`
      *,
      unit:units(*)
    `)
    .eq('id', workshopId)
    .single()
  return { data: data as UnitWorkshop | null, error }
}

export async function getWorkshopAttendance(workshopId: string): Promise<DbResult<WorkshopAttendance[]>> {
  const { data, error } = await supabase
    .from('workshop_attendance')
    .select(`
      *,
      student:students(
        *,
        profile:profiles!profile_id(first_name, last_name, email, avatar_url)
      )
    `)
    .eq('workshop_id', workshopId)
  return { data: data as WorkshopAttendance[] | null, error }
}

export async function getStudentWorkshopAttendance(studentId: string): Promise<DbResult<WorkshopAttendance[]>> {
  const { data, error } = await supabase
    .from('workshop_attendance')
    .select(`
      *,
      workshop:unit_workshops(
        *,
        unit:units(*)
      )
    `)
    .eq('student_id', studentId)
  return { data: data as WorkshopAttendance[] | null, error }
}

export async function enrollInWorkshop(workshopId: string, studentId: string): Promise<DbResult<WorkshopAttendance>> {
  const { data, error } = await supabase
    .from('workshop_attendance')
    .insert({
      workshop_id: workshopId,
      student_id: studentId,
      status: 'enrolled'
    } as never)
    .select()
    .single()
  return { data: data as WorkshopAttendance | null, error }
}

export async function markWorkshopAttendance(
  workshopId: string,
  studentId: string,
  teacherId: string,
  status: 'attended' | 'absent' | 'excused',
  notes?: string
): Promise<DbResult<WorkshopAttendance>> {
  const { data, error } = await supabase
    .from('workshop_attendance')
    .update({
      status,
      attended_at: status === 'attended' ? new Date().toISOString() : null,
      marked_by: teacherId,
      notes
    } as never)
    .eq('workshop_id', workshopId)
    .eq('student_id', studentId)
    .select()
    .single()
  return { data: data as WorkshopAttendance | null, error }
}

// ============================================
// EXÁMENES DE UNIDAD
// ============================================

export async function getUnitExam(unitId: string): Promise<DbResult<UnitExam>> {
  const { data, error } = await supabase
    .from('unit_exams')
    .select('*')
    .eq('unit_id', unitId)
    .maybeSingle()
  if (!data) return { data: null, error }
  const raw = data as any
  const mapped = {
    ...raw,
    title: raw.title_es || raw.title_en || raw.title || '',
    description: raw.description_es || raw.description_en || raw.description || null,
    time_limit: raw.time_limit_minutes ?? raw.time_limit ?? 30,
  }
  return { data: mapped as UnitExam, error }
}

export async function getExamQuestions(examId: string): Promise<DbResult<ExamQuestion[]>> {
  const { data, error } = await supabase
    .from('exam_questions')
    .select(`
      *,
      options:exam_question_options(*)
    `)
    .eq('exam_id', examId)
    .eq('is_active', true)
    .order('order_index')
  return { data: data as ExamQuestion[] | null, error }
}

export async function getExamAttempts(studentId: string, examId?: string): Promise<DbResult<ExamAttempt[]>> {
  let query = supabase
    .from('exam_attempts')
    .select('*')
    .eq('student_id', studentId)
    .order('attempt_number', { ascending: false })

  if (examId) {
    query = query.eq('exam_id', examId)
  }

  const { data, error } = await query
  return { data: data as ExamAttempt[] | null, error }
}

export async function createExamAttempt(data: {
  student_id: string
  exam_id: string
  attempt_number: number
}): Promise<DbResult<ExamAttempt>> {
  const { data: result, error } = await supabase
    .from('exam_attempts')
    .insert(data as never)
    .select()
    .single()
  return { data: result as ExamAttempt | null, error }
}

export async function submitExamAttempt(
  attemptId: string,
  data: {
    score: number
    passed: boolean
    answers: Record<string, unknown>
    time_taken_seconds: number
    completed_at: string
  }
): Promise<DbResult<ExamAttempt>> {
  const { data: result, error } = await supabase
    .from('exam_attempts')
    .update(data as never)
    .eq('id', attemptId)
    .select()
    .single()
  return { data: result as ExamAttempt | null, error }
}

// ============================================
// EXAMEN FINAL
// ============================================

export async function getFinalExam(): Promise<DbResult<FinalExam>> {
  const { data, error } = await supabase
    .from('final_exam')
    .select(`
      *,
      questions:final_exam_questions(
        *,
        options:final_exam_options(*)
      )
    `)
    .eq('is_active', true)
    .single()
  return { data: data as FinalExam | null, error }
}

export async function getFinalExamAttempts(studentId: string): Promise<DbResult<FinalExamAttempt[]>> {
  const { data, error } = await supabase
    .from('final_exam_attempts')
    .select('*')
    .eq('student_id', studentId)
    .order('attempt_number', { ascending: false })
  return { data: data as FinalExamAttempt[] | null, error }
}

export async function createFinalExamAttempt(data: {
  student_id: string
  exam_id: string
  attempt_number: number
}): Promise<DbResult<FinalExamAttempt>> {
  const { data: result, error } = await supabase
    .from('final_exam_attempts')
    .insert(data as never)
    .select()
    .single()
  return { data: result as FinalExamAttempt | null, error }
}

export async function submitFinalExamAttempt(
  attemptId: string,
  data: {
    score: number
    passed: boolean
    answers: Record<string, unknown>
    time_taken_seconds: number
    completed_at: string
  }
): Promise<DbResult<FinalExamAttempt>> {
  const { data: result, error } = await supabase
    .from('final_exam_attempts')
    .update(data as never)
    .eq('id', attemptId)
    .select()
    .single()
  return { data: result as FinalExamAttempt | null, error }
}

// ============================================
// BADGES Y GAMIFICACIÓN
// ============================================

export async function getBadges(): Promise<DbResult<Badge[]>> {
  const { data, error } = await supabase
    .from('badges')
    .select('*')
    .eq('is_active', true)
    .order('category')
  return { data: data as Badge[] | null, error }
}

export async function getStudentBadges(studentId: string): Promise<DbResult<StudentBadge[]>> {
  const { data, error } = await supabase
    .from('student_badges')
    .select(`
      *,
      badge:badges(*)
    `)
    .eq('student_id', studentId)
    .order('earned_at', { ascending: false })
  return { data: data as StudentBadge[] | null, error }
}

export async function awardBadge(studentId: string, badgeId: string): Promise<DbResult<StudentBadge>> {
  const { data, error } = await supabase
    .from('student_badges')
    .insert({
      student_id: studentId,
      badge_id: badgeId
    } as never)
    .select()
    .single()
  return { data: data as StudentBadge | null, error }
}

/**
 * Auto-checks and awards any badges the student has earned but not yet received.
 * Returns the list of newly awarded badges (empty if none).
 */
export async function checkAndAwardBadges(studentId: string): Promise<Badge[]> {
  // 1. Fetch all data in parallel
  const [badgesRes, earnedRes, lessonProgressRes, unitProgressRes, studentRes, attendanceRes] = await Promise.all([
    getBadges(),
    getStudentBadges(studentId),
    getStudentLessonProgress(studentId),
    getStudentUnitProgress(studentId),
    getStudent(studentId),
    getStudentWorkshopAttendance(studentId)
  ])

  const allBadges = badgesRes.data || []
  const earnedBadgeIds = new Set((earnedRes.data || []).map(sb => sb.badge_id))
  const unearnedBadges = allBadges.filter(b => !earnedBadgeIds.has(b.id))

  if (unearnedBadges.length === 0) return []

  // 2. Calculate metrics
  const lessonProgress = lessonProgressRes.data || []
  const unitProgress = unitProgressRes.data || []
  const student = studentRes.data
  const attendance = attendanceRes.data || []

  const lessonsCompleted = lessonProgress.filter(lp => lp.status === 'completed').length
  const contentsCompleted = lessonProgress.reduce((acc, lp) => acc + (lp.contents_completed || 0), 0)
  const unitsCompleted = unitProgress.filter(p => p.status === 'completed').length
  const streakDays = student?.streak_days || 0
  const workshopsAttended = attendance.filter(a => a.status === 'attended').length
  const examsPassed = unitProgress.filter(p => p.exam_passed).length

  // Metric lookup by requirement_type
  const metrics: Record<string, number> = {
    lessons_completed: lessonsCompleted,
    contents_completed: contentsCompleted,
    units_completed: unitsCompleted,
    streak_days: streakDays,
    workshops_attended: workshopsAttended,
    exams_passed: examsPassed,
  }

  // 3. Check each unearned badge and award if met
  const newlyAwarded: Badge[] = []

  for (const badge of unearnedBadges) {
    const currentValue = metrics[badge.requirement_type]
    if (currentValue !== undefined && currentValue >= badge.requirement_value) {
      const { error } = await awardBadge(studentId, badge.id)
      if (!error) {
        newlyAwarded.push(badge)
      }
    }
  }

  return newlyAwarded
}

// ============================================
// PUNTOS
// ============================================

export async function addPointTransaction(data: {
  student_id: string
  points: number
  transaction_type: string
  reference_id?: string
  description?: string
}): Promise<DbResult<PointTransaction>> {
  const { data: result, error } = await supabase
    .from('point_transactions')
    .insert(data as never)
    .select()
    .single()
  return { data: result as PointTransaction | null, error }
}

export async function getPointTransactions(studentId: string, limit = 20): Promise<DbResult<PointTransaction[]>> {
  const { data, error } = await supabase
    .from('point_transactions')
    .select('*')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false })
    .limit(limit)
  return { data: data as PointTransaction[] | null, error }
}

// ============================================
// LEADERBOARD
// ============================================

export async function getWeeklyLeaderboard(weekStart?: string, limit = 10): Promise<DbResult<LeaderboardEntry[]>> {
  let query = supabase
    .from('leaderboard_weekly')
    .select(`
      *,
      student:students(
        *,
        profile:profiles!profile_id(first_name, last_name, avatar_url)
      )
    `)
    .order('points_earned', { ascending: false })
    .limit(limit)

  if (weekStart) {
    query = query.eq('week_start', weekStart)
  }

  const { data, error } = await query
  return { data: data as LeaderboardEntry[] | null, error }
}

// ============================================
// CERTIFICADOS
// ============================================

export async function getStudentCertificates(studentId: string): Promise<DbResult<Certificate[]>> {
  const { data, error } = await supabase
    .from('certificates')
    .select('*')
    .eq('student_id', studentId)
    .order('issue_date', { ascending: false })
  return { data: data as Certificate[] | null, error }
}

export async function getCertificate(certificateId: string): Promise<DbResult<Certificate>> {
  const { data, error } = await supabase
    .from('certificates')
    .select('*')
    .eq('id', certificateId)
    .single()
  return { data: data as Certificate | null, error }
}

export async function getCertificateByCode(verificationCode: string): Promise<DbResult<Certificate>> {
  const { data, error } = await supabase
    .from('certificates')
    .select(`
      *,
      student:students(
        *,
        profile:profiles!profile_id(first_name, last_name)
      )
    `)
    .eq('verification_code', verificationCode)
    .single()
  return { data: data as Certificate | null, error }
}

// ============================================
// NOTIFICACIONES
// ============================================

export async function getNotifications(profileId: string, limit = 20): Promise<DbResult<Notification[]>> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('profile_id', profileId)
    .order('created_at', { ascending: false })
    .limit(limit)
  return { data: data as Notification[] | null, error }
}

export async function markNotificationAsRead(notificationId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString()
    } as never)
    .eq('id', notificationId)
  return { data: null, error }
}

export async function markAllNotificationsAsRead(profileId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString()
    } as never)
    .eq('profile_id', profileId)
    .eq('is_read', false)
  return { data: null, error }
}

// ============================================
// PERFIL Y USUARIOS
// ============================================

export async function getProfile(userId: string): Promise<DbResult<Profile>> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()
  return { data: data as Profile | null, error }
}

export async function updateProfile(userId: string, data: Partial<Profile>): Promise<DbResult<Profile>> {
  const { data: result, error } = await supabase
    .from('profiles')
    .update(data as never)
    .eq('id', userId)
    .select()
    .single()
  return { data: result as Profile | null, error }
}

export async function getStudent(studentId: string): Promise<DbResult<Student>> {
  const { data, error } = await supabase
    .from('students')
    .select(`
      *,
      profile:profiles!profile_id(*)
    `)
    .eq('id', studentId)
    .single()
  return { data: data as Student | null, error }
}

export async function getStudentByProfileId(profileId: string): Promise<DbResult<Student>> {
  const { data, error } = await supabase
    .from('students')
    .select(`
      *,
      profile:profiles!profile_id(*)
    `)
    .eq('profile_id', profileId)
    .single()
  return { data: data as Student | null, error }
}

export async function updateStudent(studentId: string, data: Partial<Student>): Promise<DbResult<Student>> {
  const { data: result, error } = await supabase
    .from('students')
    .update(data as never)
    .eq('id', studentId)
    .select()
    .single()
  return { data: result as Student | null, error }
}

export async function getTeacher(teacherId: string): Promise<DbResult<Teacher>> {
  const { data, error } = await supabase
    .from('teachers')
    .select(`
      *,
      profile:profiles!profile_id(*)
    `)
    .eq('id', teacherId)
    .single()
  return { data: data as Teacher | null, error }
}

export async function getTeacherByProfileId(profileId: string): Promise<DbResult<Teacher>> {
  const { data, error } = await supabase
    .from('teachers')
    .select(`
      *,
      profile:profiles!profile_id(*)
    `)
    .eq('profile_id', profileId)
    .single()
  return { data: data as Teacher | null, error }
}

// ============================================
// ESTADÍSTICAS
// ============================================

export async function getStudentStats(studentId: string): Promise<DbResult<StudentStats>> {
  const { data: student } = await getStudent(studentId)
  const { data: unitProgress } = await getStudentUnitProgress(studentId)
  const { data: badges } = await getStudentBadges(studentId)
  const { data: attendance } = await getStudentWorkshopAttendance(studentId)

  // Calcular estadísticas
  const unitsCompleted = unitProgress?.filter(p => p.status === 'completed').length || 0
  const lessonsCompleted = unitProgress?.reduce((acc, p) => acc + p.lessons_completed, 0) || 0
  const examsPassed = unitProgress?.filter(p => p.exam_passed).length || 0
  const workshopsAttended = attendance?.filter(a => a.status === 'attended').length || 0
  const avgExamScore = unitProgress?.filter(p => p.exam_score !== null)
    .reduce((acc, p, _, arr) => acc + (p.exam_score || 0) / arr.length, 0) || 0

  const stats: StudentStats = {
    totalPoints: student?.total_points || 0,
    streakDays: student?.streak_days || 0,
    unitsCompleted,
    lessonsCompleted,
    contentsCompleted: 0, // Would need to calculate from content progress
    exercisesCompleted: 0, // Would need to calculate from exercise results
    workshopsAttended,
    examsPassed,
    badgesEarned: badges?.length || 0,
    averageExamScore: avgExamScore,
    timeSpentSeconds: 0 // Would need to calculate from content progress
  }

  return { data: stats, error: null }
}

// ============================================
// CONTADORES
// ============================================

export async function getTotalUnitsCount(): Promise<number> {
  const { count } = await supabase
    .from('units')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', true)
  return count || 0
}

export async function getTotalLessonsCount(): Promise<number> {
  const { count } = await supabase
    .from('lessons')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', true)
  return count || 0
}

export async function getTotalContentsCount(): Promise<number> {
  const { count } = await supabase
    .from('contents')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', true)
  return count || 0
}

export async function getTotalExercisesCount(): Promise<number> {
  const { count } = await supabase
    .from('exercises')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', true)
  return count || 0
}

// ============================================
// ADMIN: CRUD OPERATIONS
// ============================================

// Units
export async function createUnit(data: Partial<Unit>): Promise<DbResult<Unit>> {
  const { data: result, error } = await supabase
    .from('units')
    .insert(data as never)
    .select()
    .single()
  return { data: result as Unit | null, error }
}

export async function updateUnit(unitId: string, data: Partial<Unit>): Promise<DbResult<Unit>> {
  const { data: result, error } = await supabase
    .from('units')
    .update(data as never)
    .eq('id', unitId)
    .select()
    .single()
  return { data: result as Unit | null, error }
}

export async function deleteUnit(unitId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('units')
    .update({ is_active: false } as never)
    .eq('id', unitId)
  return { data: null, error }
}

// Lessons
export async function createLesson(data: Partial<Lesson>): Promise<DbResult<Lesson>> {
  const { data: result, error } = await supabase
    .from('lessons')
    .insert(data as never)
    .select()
    .single()
  return { data: result as Lesson | null, error }
}

export async function updateLesson(lessonId: string, data: Partial<Lesson>): Promise<DbResult<Lesson>> {
  const { data: result, error } = await supabase
    .from('lessons')
    .update(data as never)
    .eq('id', lessonId)
    .select()
    .single()
  return { data: result as Lesson | null, error }
}

export async function deleteLesson(lessonId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('lessons')
    .update({ is_active: false } as never)
    .eq('id', lessonId)
  return { data: null, error }
}

// Contents
export async function createContent(data: Partial<Content>): Promise<DbResult<Content>> {
  const { data: result, error } = await supabase
    .from('contents')
    .insert(data as never)
    .select()
    .single()
  return { data: result as Content | null, error }
}

export async function updateContent(contentId: string, data: Partial<Content>): Promise<DbResult<Content>> {
  const { data: result, error } = await supabase
    .from('contents')
    .update(data as never)
    .eq('id', contentId)
    .select()
    .single()
  return { data: result as Content | null, error }
}

export async function deleteContent(contentId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('contents')
    .update({ is_active: false } as never)
    .eq('id', contentId)
  return { data: null, error }
}

// Exercises
export async function createExercise(data: Partial<Exercise>): Promise<DbResult<Exercise>> {
  const { data: result, error } = await supabase
    .from('exercises')
    .insert(data as never)
    .select()
    .single()
  return { data: result as Exercise | null, error }
}

export async function updateExercise(exerciseId: string, data: Partial<Exercise>): Promise<DbResult<Exercise>> {
  const { data: result, error } = await supabase
    .from('exercises')
    .update(data as never)
    .eq('id', exerciseId)
    .select()
    .single()
  return { data: result as Exercise | null, error }
}

export async function deleteExercise(exerciseId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('exercises')
    .update({ is_active: false } as never)
    .eq('id', exerciseId)
  return { data: null, error }
}

// Exercise Options
export async function createExerciseOption(data: Partial<ExerciseOption>): Promise<DbResult<ExerciseOption>> {
  const { data: result, error } = await supabase
    .from('exercise_options')
    .insert(data as never)
    .select()
    .single()
  return { data: result as ExerciseOption | null, error }
}

export async function updateExerciseOption(optionId: string, data: Partial<ExerciseOption>): Promise<DbResult<ExerciseOption>> {
  const { data: result, error } = await supabase
    .from('exercise_options')
    .update(data as never)
    .eq('id', optionId)
    .select()
    .single()
  return { data: result as ExerciseOption | null, error }
}

export async function deleteExerciseOption(optionId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('exercise_options')
    .delete()
    .eq('id', optionId)
  return { data: null, error }
}

// Exercise Answers
export async function createExerciseAnswer(data: Partial<ExerciseAnswer>): Promise<DbResult<ExerciseAnswer>> {
  const { data: result, error } = await supabase
    .from('exercise_answers')
    .insert(data as never)
    .select()
    .single()
  return { data: result as ExerciseAnswer | null, error }
}

export async function updateExerciseAnswer(answerId: string, data: Partial<ExerciseAnswer>): Promise<DbResult<ExerciseAnswer>> {
  const { data: result, error } = await supabase
    .from('exercise_answers')
    .update(data as never)
    .eq('id', answerId)
    .select()
    .single()
  return { data: result as ExerciseAnswer | null, error }
}

export async function deleteExerciseAnswer(answerId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('exercise_answers')
    .delete()
    .eq('id', answerId)
  return { data: null, error }
}

// ============================================
// ADMIN: WORKSHOP & EXAM MANAGEMENT
// ============================================

export async function createUnitWorkshop(data: Partial<UnitWorkshop>): Promise<DbResult<UnitWorkshop>> {
  const { data: result, error } = await supabase
    .from('unit_workshops')
    .insert(data as never)
    .select()
    .single()
  return { data: result as UnitWorkshop | null, error }
}

export async function updateUnitWorkshop(workshopId: string, data: Partial<UnitWorkshop>): Promise<DbResult<UnitWorkshop>> {
  const { data: result, error } = await supabase
    .from('unit_workshops')
    .update(data as never)
    .eq('id', workshopId)
    .select()
    .single()
  return { data: result as UnitWorkshop | null, error }
}

export async function createUnitExam(data: Partial<UnitExam>): Promise<DbResult<UnitExam>> {
  const { data: result, error } = await supabase
    .from('unit_exams')
    .insert(data as never)
    .select()
    .single()
  return { data: result as UnitExam | null, error }
}

export async function updateUnitExam(examId: string, data: Partial<UnitExam>): Promise<DbResult<UnitExam>> {
  const { data: result, error } = await supabase
    .from('unit_exams')
    .update(data as never)
    .eq('id', examId)
    .select()
    .single()
  return { data: result as UnitExam | null, error }
}

export async function createExamQuestion(data: Partial<ExamQuestion>): Promise<DbResult<ExamQuestion>> {
  const { data: result, error } = await supabase
    .from('exam_questions')
    .insert(data as never)
    .select()
    .single()
  return { data: result as ExamQuestion | null, error }
}

export async function updateExamQuestion(questionId: string, data: Partial<ExamQuestion>): Promise<DbResult<ExamQuestion>> {
  const { data: result, error } = await supabase
    .from('exam_questions')
    .update(data as never)
    .eq('id', questionId)
    .select()
    .single()
  return { data: result as ExamQuestion | null, error }
}

export async function deleteExamQuestion(questionId: string): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('exam_questions')
    .update({ is_active: false } as never)
    .eq('id', questionId)
  return { data: null, error }
}

// ============================================
// TEACHER: GET ALL STUDENTS FOR WORKSHOP
// ============================================

export async function getStudentsForWorkshop(workshopId: string): Promise<DbResult<Student[]>> {
  const { data, error } = await supabase
    .from('workshop_attendance')
    .select(`
      student:students(
        *,
        profile:profiles!profile_id(first_name, last_name, email, avatar_url)
      )
    `)
    .eq('workshop_id', workshopId)

  const students = data?.map(item => (item as { student: Student }).student).filter(Boolean) || []
  return { data: students, error }
}

export async function getAllStudents(): Promise<DbResult<Student[]>> {
  const { data, error } = await supabase
    .from('students')
    .select(`
      *,
      profile:profiles!profile_id(first_name, last_name, email, avatar_url)
    `)
    .order('created_at', { ascending: false })
  return { data: data as Student[] | null, error }
}

export async function getTeacherWorkshops(teacherId: string): Promise<DbResult<UnitWorkshop[]>> {
  const { data, error } = await supabase
    .from('unit_workshops')
    .select(`
      *,
      unit:units(*),
      cohort:cohorts(id, name),
      attendance:workshop_attendance(
        *,
        student:students(
          *,
          profile:profiles!profile_id(first_name, last_name, avatar_url)
        )
      )
    `)
    .eq('teacher_id', teacherId)
    .order('scheduled_date', { ascending: true })
  return { data: data as UnitWorkshop[] | null, error }
}

// ============================================
// FUNCIONES ADICIONALES PARA EJERCICIOS
// ============================================

export async function getExercisesByLesson(lessonId: string): Promise<DbResult<Exercise[]>> {
  const { data, error } = await supabase
    .from('exercises')
    .select(`
      *,
      content:contents!inner(lesson_id)
    `)
    .eq('content.lesson_id', lessonId)
    .eq('is_active', true)
    .order('order_num')
  return { data: data as Exercise[] | null, error }
}

export async function updateStudentPoints(studentId: string, points: number): Promise<DbResult<null>> {
  const { error } = await (supabase
    .from('students') as any)
    .update({ total_points: points })
    .eq('id', studentId)
  return { data: null, error }
}

// ============================================
// TEACHER: MODULE QUERIES
// ============================================

/**
 * Obtener cohortes asignadas al profesor.
 * NOTA: cohorts.teacher_id = profiles.id (NO teachers.id)
 */
export async function getTeacherCohorts(profileId: string): Promise<DbResult<Cohort[]>> {
  const { data, error } = await (supabase
    .from('cohorts') as any)
    .select('*, level:levels(*)')
    .eq('teacher_id', profileId)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
  return { data: data as Cohort[] | null, error }
}

/**
 * Obtener perfiles de alumnos de las cohortes del profesor.
 * student_ids en cohorts son profile UUIDs.
 */
export async function getTeacherStudentProfiles(profileId: string): Promise<DbResult<Profile[]>> {
  // 1. Obtener cohortes del profesor
  const { data: cohorts, error: cohortsError } = await getTeacherCohorts(profileId)
  if (cohortsError || !cohorts) return { data: null, error: cohortsError }

  // 2. Recolectar student_ids únicos (son profile UUIDs)
  const allStudentIds = [...new Set(cohorts.flatMap(c => c.student_ids || []))]
  if (allStudentIds.length === 0) return { data: [], error: null }

  // 3. Fetch perfiles
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .in('id', allStudentIds)
  return { data: data as Profile[] | null, error }
}

/**
 * Obtener resultados de ejercicios de alumnos de las cohortes del profesor.
 * Incluye join a ejercicio y perfil del alumno.
 */
export async function getTeacherExerciseResults(profileId: string): Promise<DbResult<any[]>> {
  // 1. Obtener student profile IDs de cohortes
  const { data: cohorts, error: cohortsError } = await getTeacherCohorts(profileId)
  if (cohortsError || !cohorts) return { data: null, error: cohortsError }

  const studentProfileIds = [...new Set(cohorts.flatMap(c => c.student_ids || []))]
  if (studentProfileIds.length === 0) return { data: [], error: null }

  // 2. Obtener student IDs (de la tabla students, no profiles)
  const { data: students, error: studentsError } = await supabase
    .from('students')
    .select('id, profile_id')
    .in('profile_id', studentProfileIds)
  if (studentsError || !students) return { data: null, error: studentsError }

  const studentIds = (students as { id: string; profile_id: string }[]).map(s => s.id)
  if (studentIds.length === 0) return { data: [], error: null }

  // 3. Fetch exercise_results con joins
  const { data, error } = await supabase
    .from('exercise_results')
    .select(`
      *,
      exercise:exercises(id, title, exercise_type, skill, points, content_id),
      student:students(id, profile_id, profile:profiles!profile_id(first_name, last_name, email, avatar_url))
    `)
    .in('student_id', studentIds)
    .order('created_at', { ascending: false })
  return { data, error }
}

/**
 * Enviar revisión/feedback de un profesor sobre un resultado de ejercicio.
 */
export async function submitExerciseReview(
  resultId: string,
  reviewerId: string,
  feedback: string,
  score: number | null
): Promise<DbResult<null>> {
  const { error } = await supabase
    .from('exercise_results')
    .update({
      teacher_feedback: feedback,
      teacher_score: score,
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString()
    } as never)
    .eq('id', resultId)
  return { data: null, error }
}

// ============================================
// SYNC: Recalculate all progress from content level up
// Call this on page load to fix any stale data
// ============================================

export async function syncStudentProgress(studentId: string): Promise<void> {
  try {
    // Use the RPC function which handles enum types properly in SQL
    const { error } = await (supabase.rpc as any)('sync_student_progress', {
      p_student_id: studentId
    })

    if (error) {
      // RPC not available (not deployed yet) - log and skip
      console.warn('[SYNC] RPC sync_student_progress failed:', error.message)
    }
  } catch (err) {
    console.error('syncStudentProgress: unexpected error', err)
  }
}

// ==================== TALLERES GRABADOS ====================

// Obtener todos los talleres con grabación (para admin)
export async function getRecordedWorkshops(): Promise<DbResult<UnitWorkshop[]>> {
  const { data, error } = await (supabase
    .from('unit_workshops') as any)
    .select('*, unit:units(id, title), cohort:cohorts(id, name)')
    .not('recording_url', 'is', null)
    .neq('recording_url', '')
    .order('updated_at', { ascending: false })

  if (error) console.error('Error fetching recorded workshops:', error)
  return { data: data || [], error }
}

// Obtener talleres grabados filtrados por cohorte (para alumnos)
export async function getRecordedWorkshopsForCohort(cohortId: string): Promise<DbResult<UnitWorkshop[]>> {
  const { data, error } = await (supabase
    .from('unit_workshops') as any)
    .select('*, unit:units(id, title), cohort:cohorts(id, name)')
    .eq('cohort_id', cohortId)
    .not('recording_url', 'is', null)
    .neq('recording_url', '')
    .order('scheduled_date', { ascending: false })

  if (error) console.error('Error fetching recorded workshops for cohort:', cohortId, error)
  return { data: data || [], error }
}

// Obtener talleres grabados filtrados por múltiples cohortes
export async function getRecordedWorkshopsForCohorts(cohortIds: string[]): Promise<DbResult<UnitWorkshop[]>> {
  if (cohortIds.length === 0) return { data: [], error: null }

  const { data, error } = await (supabase
    .from('unit_workshops') as any)
    .select('*, unit:units(id, title), cohort:cohorts(id, name)')
    .in('cohort_id', cohortIds)
    .not('recording_url', 'is', null)
    .neq('recording_url', '')
    .order('scheduled_date', { ascending: false })

  if (error) console.error('Error fetching recorded workshops for cohorts:', error)
  return { data: data || [], error }
}

// Actualizar grabación de un taller
export async function updateWorkshopRecording(
  workshopId: string,
  recordingUrl: string
): Promise<DbResult<UnitWorkshop>> {
  const update: Record<string, unknown> = {
    recording_url: recordingUrl,
    updated_at: new Date().toISOString()
  }

  const { data, error } = await (supabase
    .from('unit_workshops') as any)
    .update(update)
    .eq('id', workshopId)
    .select()
    .single()

  return { data, error }
}

// Eliminar grabación de un taller
export async function deleteWorkshopRecording(workshopId: string): Promise<DbResult<UnitWorkshop>> {
  // Usar update sin .select().single() para evitar problemas con RLS
  const { error: updateError } = await (supabase
    .from('unit_workshops') as any)
    .update({
      recording_url: null,
      updated_at: new Date().toISOString()
    })
    .eq('id', workshopId)

  if (updateError) {
    return { data: null, error: updateError }
  }

  // Verificar que realmente se eliminó
  const { data: check, error: checkError } = await (supabase
    .from('unit_workshops') as any)
    .select('id, recording_url')
    .eq('id', workshopId)
    .single()

  if (checkError) {
    return { data: null, error: checkError }
  }

  if (check?.recording_url !== null) {
    return {
      data: null,
      error: { message: 'No se pudo eliminar el enlace. Verifica los permisos de admin en la base de datos.' } as any
    }
  }

  return { data: check, error: null }
}

// ============================================
// Workshop Content (material interactivo para talleres)
// ============================================

// Obtener todos los workshop_contents con su unidad
export async function getWorkshopContents(): Promise<DbResult<WorkshopContent[]>> {
  const { data, error } = await (supabase
    .from('workshop_contents') as any)
    .select('*, unit:units(id, unit_number, title, name_es, name_en)')
    .order('created_at', { ascending: true })

  return { data: data || [], error }
}

// Obtener un workshop_content con sus bloques
export async function getWorkshopContentWithBlocks(contentId: string): Promise<DbResult<WorkshopContent>> {
  const { data, error } = await (supabase
    .from('workshop_contents') as any)
    .select('*, unit:units(id, unit_number, title, name_es, name_en), blocks:workshop_blocks(*)')
    .eq('id', contentId)
    .single()

  if (data?.blocks) {
    data.blocks = (data.blocks as any[]).sort((a: any, b: any) => a.sort_order - b.sort_order)
  }

  return { data, error }
}

// Obtener workshop_content por unit_id
export async function getWorkshopContentByUnit(unitId: string): Promise<DbResult<WorkshopContent>> {
  const { data, error } = await (supabase
    .from('workshop_contents') as any)
    .select('*, blocks:workshop_blocks(*)')
    .eq('unit_id', unitId)
    .single()

  if (data?.blocks) {
    data.blocks = (data.blocks as any[]).sort((a: any, b: any) => a.sort_order - b.sort_order)
  }

  return { data, error }
}

// Crear workshop_content
export async function createWorkshopContent(content: {
  unit_id: string
  title: string
  title_en?: string
  description?: string
  estimated_duration_minutes?: number
  is_published?: boolean
}): Promise<DbResult<WorkshopContent>> {
  const { data, error } = await (supabase
    .from('workshop_contents') as any)
    .insert({
      ...content,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select('*, unit:units(id, unit_number, title, name_es, name_en)')
    .single()

  return { data, error }
}

// Actualizar workshop_content
export async function updateWorkshopContent(
  contentId: string,
  updates: Partial<Pick<WorkshopContent, 'title' | 'title_en' | 'description' | 'estimated_duration_minutes' | 'is_published'>>
): Promise<DbResult<WorkshopContent>> {
  const { data, error } = await (supabase
    .from('workshop_contents') as any)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', contentId)
    .select()
    .single()

  return { data, error }
}

// Eliminar workshop_content (y sus bloques por CASCADE)
export async function deleteWorkshopContent(contentId: string): Promise<DbResult<null>> {
  const { error } = await (supabase
    .from('workshop_contents') as any)
    .delete()
    .eq('id', contentId)

  return { data: null, error }
}

// ============================================
// Workshop Blocks
// ============================================

// Crear un bloque
export async function createWorkshopBlock(block: {
  workshop_content_id: string
  sort_order: number
  section?: string
  block_type: string
  title?: string
  instructions?: string
  teacher_notes?: string
  config: Record<string, unknown>
  estimated_minutes?: number
}): Promise<DbResult<WorkshopBlock>> {
  const { data, error } = await (supabase
    .from('workshop_blocks') as any)
    .insert({
      ...block,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select()
    .single()

  return { data, error }
}

// Actualizar un bloque
export async function updateWorkshopBlock(
  blockId: string,
  updates: Partial<Pick<WorkshopBlock, 'sort_order' | 'section' | 'block_type' | 'title' | 'instructions' | 'teacher_notes' | 'config' | 'estimated_minutes'>>
): Promise<DbResult<WorkshopBlock>> {
  const { data, error } = await (supabase
    .from('workshop_blocks') as any)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', blockId)
    .select()
    .single()

  return { data, error }
}

// Eliminar un bloque
export async function deleteWorkshopBlock(blockId: string): Promise<DbResult<null>> {
  const { error } = await (supabase
    .from('workshop_blocks') as any)
    .delete()
    .eq('id', blockId)

  return { data: null, error }
}

// Reordenar bloques
export async function reorderWorkshopBlocks(
  blocks: { id: string; sort_order: number }[]
): Promise<DbResult<null>> {
  for (const block of blocks) {
    const { error } = await (supabase
      .from('workshop_blocks') as any)
      .update({ sort_order: block.sort_order, updated_at: new Date().toISOString() })
      .eq('id', block.id)

    if (error) return { data: null, error }
  }

  return { data: null, error: null }
}

// Obtener talleres publicados (para profesor)
export async function getPublishedWorkshopContents(): Promise<DbResult<WorkshopContent[]>> {
  const { data, error } = await (supabase
    .from('workshop_contents') as any)
    .select('*, unit:units(id, unit_number, title, name_es, name_en), blocks:workshop_blocks(id)')
    .eq('is_published', true)
    .order('created_at', { ascending: true })

  return { data: data || [], error }
}

// ============================================
// FLUJOS DE AUTOMATIZACION
// ============================================

export async function getAutomationFlows(): Promise<DbResult<AutomationFlow[]>> {
  const { data, error } = await (supabase
    .from('automation_flows') as any)
    .select('*, actions:automation_actions(*)')
    .order('created_at', { ascending: false })
  return { data: data || [], error }
}

export async function getAutomationFlow(id: string): Promise<DbResult<AutomationFlow>> {
  const { data, error } = await (supabase
    .from('automation_flows') as any)
    .select('*, actions:automation_actions(*)')
    .eq('id', id)
    .single()
  return { data, error }
}

export async function createAutomationFlow(flow: {
  name: string
  description?: string
  trigger_type: string
  trigger_config: Record<string, any>
  scope_type: string
  scope_id?: string | null
  is_active?: boolean
}): Promise<DbResult<AutomationFlow>> {
  const { data, error } = await (supabase
    .from('automation_flows') as any)
    .insert({ ...flow, created_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .select()
    .single()
  return { data, error }
}

export async function updateAutomationFlow(id: string, updates: Partial<AutomationFlow>): Promise<DbResult<AutomationFlow>> {
  const { data, error } = await (supabase
    .from('automation_flows') as any)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

export async function deleteAutomationFlow(id: string): Promise<DbResult<null>> {
  const { error } = await (supabase.from('automation_flows') as any).delete().eq('id', id)
  return { data: null, error }
}

export async function setAutomationActions(flowId: string, actions: {
  sort_order: number
  action_type: string
  action_config: Record<string, any>
}[]): Promise<DbResult<AutomationAction[]>> {
  await (supabase.from('automation_actions') as any).delete().eq('flow_id', flowId)
  if (actions.length === 0) return { data: [], error: null }
  const { data, error } = await (supabase
    .from('automation_actions') as any)
    .insert(actions.map(a => ({ ...a, flow_id: flowId, created_at: new Date().toISOString() })))
    .select()
  return { data: data || [], error }
}

export async function getAutomationLogs(flowId?: string, limit = 100): Promise<DbResult<AutomationLog[]>> {
  let query = (supabase
    .from('automation_logs') as any)
    .select('*, flow:automation_flows(id, name, trigger_type)')
    .order('executed_at', { ascending: false })
    .limit(limit)
  if (flowId) query = query.eq('flow_id', flowId)
  const { data, error } = await query
  return { data: data || [], error }
}

// ============================================
// ENCUESTAS
// ============================================

export async function getSurveys(): Promise<DbResult<Survey[]>> {
  const { data, error } = await (supabase
    .from('surveys') as any)
    .select('*')
    .order('created_at', { ascending: false })
  return { data: data || [], error }
}

export async function getSurvey(id: string): Promise<DbResult<Survey>> {
  const { data, error } = await (supabase
    .from('surveys') as any)
    .select('*')
    .eq('id', id)
    .single()
  return { data, error }
}

export async function createSurvey(survey: {
  title: string
  description?: string
  questions: any[]
  is_active?: boolean
}): Promise<DbResult<Survey>> {
  const { data, error } = await (supabase
    .from('surveys') as any)
    .insert({ ...survey, created_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .select()
    .single()
  return { data, error }
}

export async function updateSurvey(id: string, updates: Partial<Survey>): Promise<DbResult<Survey>> {
  const { data, error } = await (supabase
    .from('surveys') as any)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

export async function deleteSurvey(id: string): Promise<DbResult<null>> {
  const { error } = await (supabase.from('surveys') as any).delete().eq('id', id)
  return { data: null, error }
}

export async function getSurveyResponses(surveyId: string): Promise<DbResult<SurveyResponse[]>> {
  const { data, error } = await (supabase
    .from('survey_responses') as any)
    .select('*')
    .eq('survey_id', surveyId)
    .order('completed_at', { ascending: false })
  return { data: data || [], error }
}

// ============================================
// POPUPS DE ALUMNOS
// ============================================

export async function getStudentPopups(studentId: string): Promise<DbResult<StudentPopup[]>> {
  const { data, error } = await (supabase
    .from('student_popups') as any)
    .select('*, survey:surveys(id, title, description, questions)')
    .eq('student_id', studentId)
    .eq('is_read', false)
    .order('created_at', { ascending: false })
  return { data: data || [], error }
}

export async function markPopupRead(popupId: string): Promise<DbResult<null>> {
  const { error } = await (supabase
    .from('student_popups') as any)
    .update({ is_read: true })
    .eq('id', popupId)
  return { data: null, error }
}

export async function submitSurveyResponse(response: {
  survey_id: string
  student_id: string
  answers: Record<string, any>
}): Promise<DbResult<SurveyResponse>> {
  const { data, error } = await (supabase
    .from('survey_responses') as any)
    .insert({ ...response, completed_at: new Date().toISOString() })
    .select()
    .single()
  return { data, error }
}
